<template>
  <div class="shorts-player" :class="{ 'recording-active': recordingPhase === 'active' }">
    <!-- Top bar -->
    <div class="top-bar" v-show="recordingPhase !== 'active'">
      <div class="top-bar-left">
        <button class="btn-icon" @click="goBack" title="Back">‹</button>
        <div class="title-block">
          <div class="collection-title">{{ collectionTitle || 'Shorts Player' }}</div>
          <div class="reel-title">{{ activeReel ? activeReel.title : 'No reel loaded' }}</div>
        </div>
      </div>
      <div class="top-bar-center">
        <button class="btn" :disabled="!activeReel" @click="restart">Restart</button>
        <span class="time-readout">{{ formatTime(audioCurrent) }} / {{ formatTime(audioDuration || (activeReel ? activeReel.duration_ms / 1000 : 0)) }}</span>
        <span v-if="audioMissing" class="audio-warn">audio missing — silent</span>
      </div>
      <div class="top-bar-right">
        <button
          class="btn primary"
          :disabled="!activeReel || recordingPhase !== 'idle'"
          @click="startRecording">
          <span v-if="recordingPhase === 'countdown'">Starting in {{ countdown }}…</span>
          <span v-else-if="recordingPhase === 'active'">Recording…</span>
          <span v-else>Record &amp; Download</span>
        </button>
        <button class="btn" v-if="recordingPhase === 'active'" @click="stopRecording">Stop</button>
      </div>
    </div>

    <!-- Layout -->
    <div class="layout">
      <div class="stage-wrap" ref="stageWrap">
        <div class="stage-outer" :style="stageOuterStyle">
          <div class="stage-inner" ref="stageInner">
            <iframe
              v-if="bundleUrl"
              ref="bundleFrame"
              :src="bundleUrl"
              class="bundle-frame"
              sandbox="allow-scripts allow-same-origin"
              @load="onBundleLoad"
            />
            <div class="empty-state" v-else>
              <div class="empty-title">No reel selected</div>
              <div class="empty-sub">Pick a reel from the right.</div>
            </div>

            <div class="countdown-overlay" v-if="recordingPhase === 'countdown'">
              <div class="countdown-num">{{ countdown }}</div>
              <div class="countdown-hint">Recording starts at 0</div>
            </div>
          </div>
        </div>

        <audio
          ref="audio"
          class="reel-audio"
          :src="audioSrc"
          preload="auto"
          crossorigin="anonymous"
          @loadedmetadata="onAudioLoaded"
          @timeupdate="onAudioTimeUpdate"
          @ended="onAudioEnded"
          @error="onAudioError"
        />
      </div>

      <!-- Sidebar -->
      <aside class="sidebar" v-show="recordingPhase !== 'active'">
        <div class="sidebar-header">{{ reels.length }} reel{{ reels.length === 1 ? '' : 's' }}</div>
        <div class="reel-list" v-if="reels.length">
          <button
            v-for="(reel, i) in reels"
            :key="reel.id || i"
            class="reel-item"
            :class="{ active: activeReelIndex === i }"
            @click="selectReel(i)">
            <div class="reel-item-index">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="reel-item-body">
              <div class="reel-item-title">{{ reel.title || reel.id }}</div>
              <div class="reel-item-meta">
                <span v-if="reel.duration_ms">{{ Math.round(reel.duration_ms / 1000) }}s</span>
                <span v-if="reel.cards"> · {{ reel.cards.length }} cards</span>
              </div>
            </div>
          </button>
        </div>
        <div v-else class="reel-empty-list">No reels in <code>video_shorts</code>.</div>

        <div class="manifest-paste">
          <div class="manifest-paste-label">Paste manifest JSON (override):</div>
          <textarea v-model="pastedManifest" rows="3" placeholder='{"video_shorts": [...]}' />
          <div class="manifest-paste-actions">
            <button class="btn small" @click="loadPastedManifest">Load</button>
            <button class="btn small ghost" @click="pastedManifest = ''">Clear</button>
          </div>
          <div v-if="pasteError" class="manifest-paste-error">{{ pasteError }}</div>
        </div>

        <div class="hint">
          <kbd>Space</kbd> restart · <kbd>↑/↓</kbd> switch reel · <kbd>Esc Esc</kbd> stop recording
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'

function pad2(n) { return String(n).padStart(2, '0') }
function slugify(s) {
  return String(s || 'shorts')
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'shorts'
}
function timestamp() {
  const d = new Date()
  return d.getFullYear() + pad2(d.getMonth() + 1) + pad2(d.getDate()) + '-' +
         pad2(d.getHours()) + pad2(d.getMinutes()) + pad2(d.getSeconds())
}

export default {
  name: 'ShortsPlayer',
  data() {
    return {
      collectionId: '',
      collectionTitle: '',
      reels: [],
      activeReelIndex: -1,
      bundleUrl: '',
      bundleObjectUrls: [],

      // Audio
      audioSrc: '',
      audioCurrent: 0,
      audioDuration: 0,
      audioMissing: false,

      // Recording
      recordingPhase: 'idle', // 'idle' | 'countdown' | 'active'
      countdown: 4,
      countdownTimer: null,
      mediaRecorder: null,
      recordedChunks: [],
      captureStream: null,
      audioCtx: null,
      audioSourceNode: null,
      audioStreamDest: null,
      escTapTime: 0,
      reelTimeoutId: null,

      // Stage scaling
      stageScale: 1,
      stageRO: null,

      // Manifest paste fallback
      pastedManifest: '',
      pasteError: ''
    }
  },
  computed: {
    activeReel() {
      if (this.activeReelIndex < 0 || this.activeReelIndex >= this.reels.length) return null
      return this.reels[this.activeReelIndex]
    },
    collectionSlug() { return slugify(this.collectionTitle) },
    stageOuterStyle() {
      return { transform: `scale(${this.stageScale})`, transformOrigin: 'center center' }
    }
  },
  watch: {
    activeReelIndex() { this.loadActiveReel() }
  },
  mounted() {
    const q = this.$route.query || {}
    this.collectionId = q.collection_id || q.id || ''
    const reelId = q.reel_id || ''

    this.loadCollection().then(() => {
      if (reelId && this.reels.length) {
        const idx = this.reels.findIndex(r => r.id === reelId)
        if (idx >= 0) this.activeReelIndex = idx
      }
    })

    this.setupStageScaling()
    window.addEventListener('keydown', this.onKeyDown, true)
    window.addEventListener('message', this.onBundleMessage)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown, true)
    window.removeEventListener('message', this.onBundleMessage)
    this.teardownStageScaling()
    this.cleanupRecording()
    this.bundleObjectUrls.forEach(u => { try { URL.revokeObjectURL(u) } catch (_) { /* noop */ } })
    this.bundleObjectUrls = []
    if (this.reelTimeoutId) clearTimeout(this.reelTimeoutId)
  },
  methods: {
    /* ── Data loading ── */
    async loadCollection() {
      if (!this.collectionId) return
      try {
        const resp = await postRequest('getPostCollection', {
          id: this.collectionId,
          collection_id: this.collectionId,
          user_id: (this.$store && this.$store.state && this.$store.state.user_id) || ''
        })
        const data = resp && resp.data
        if (!data || data.code !== 0) return
        const coll = data.data || {}
        this.collectionTitle = coll.title || ''
        const shorts = coll.video_shorts
        if (Array.isArray(shorts) && shorts.length) {
          this.reels = shorts
          if (this.activeReelIndex < 0) this.activeReelIndex = 0
        }
      } catch (err) {
        console.warn('[ShortsPlayer] loadCollection failed', err)
      }
    },
    loadPastedManifest() {
      this.pasteError = ''
      const text = (this.pastedManifest || '').trim()
      if (!text) { this.pasteError = 'Paste a manifest first.'; return }
      try {
        const obj = JSON.parse(text)
        const list = Array.isArray(obj.video_shorts) ? obj.video_shorts : (Array.isArray(obj) ? obj : null)
        if (!list || !list.length) { this.pasteError = 'Manifest must contain a video_shorts array.'; return }
        this.reels = list
        this.activeReelIndex = 0
      } catch (e) {
        this.pasteError = 'Invalid JSON: ' + (e.message || e)
      }
    },

    /* ── Active reel loading ── */
    loadActiveReel() {
      this.audioCurrent = 0
      this.audioDuration = 0
      this.audioMissing = false
      if (this.reelTimeoutId) { clearTimeout(this.reelTimeoutId); this.reelTimeoutId = null }

      const reel = this.activeReel
      if (!reel) {
        this.bundleUrl = ''
        this.audioSrc = ''
        return
      }

      // Bundle: HTML string → Blob URL (revoke prior)
      if (reel.bundle_html) {
        const blob = new Blob([reel.bundle_html], { type: 'text/html' })
        const url = URL.createObjectURL(blob)
        this.bundleObjectUrls.push(url)
        this.bundleUrl = url
      } else if (reel.bundle_url) {
        this.bundleUrl = reel.bundle_url
      } else {
        this.bundleUrl = ''
      }

      // Audio: per-reel audio_ref or collection-default convention
      const audioPath = reel.audio_ref || `/audio/${this.collectionSlug || 'shorts'}/shorts.mp3`
      this.audioSrc = audioPath
    },
    onBundleLoad() {
      // Bundle's GSAP timeline auto-plays. Start audio in lockstep.
      this.$nextTick(() => {
        const audio = this.$refs.audio
        if (audio && this.audioSrc) {
          try { audio.currentTime = 0; const p = audio.play(); if (p && p.catch) p.catch(() => {}) } catch (_) { /* noop */ }
        }
      })
      // Fallback hard duration cap — if the bundle never posts reel_complete,
      // stop recording at the reel's declared duration.
      if (this.recordingPhase === 'active' && this.activeReel && this.activeReel.duration_ms) {
        if (this.reelTimeoutId) clearTimeout(this.reelTimeoutId)
        this.reelTimeoutId = setTimeout(() => {
          if (this.recordingPhase === 'active') this.stopRecording()
        }, this.activeReel.duration_ms + 500)
      }
    },
    onBundleMessage(ev) {
      const data = ev && ev.data
      if (!data || typeof data !== 'object') return
      if (data.type === 'reel_complete') {
        if (this.recordingPhase === 'active') {
          // Small tail to avoid clipping the final fade
          setTimeout(() => this.stopRecording(), 300)
        }
      }
    },

    /* ── Audio handlers ── */
    onAudioLoaded() {
      const a = this.$refs.audio
      this.audioDuration = (a && a.duration) || 0
    },
    onAudioTimeUpdate() {
      const a = this.$refs.audio
      this.audioCurrent = (a && a.currentTime) || 0
    },
    onAudioEnded() { /* bundle drives reel completion via postMessage */ },
    onAudioError() { this.audioMissing = true },

    /* ── Stage scaling ── */
    setupStageScaling() {
      this.updateScale()
      const wrap = this.$refs.stageWrap
      if (!wrap) return
      if (typeof ResizeObserver !== 'undefined') {
        this.stageRO = new ResizeObserver(() => this.updateScale())
        this.stageRO.observe(wrap)
      } else {
        window.addEventListener('resize', this.updateScale)
      }
    },
    teardownStageScaling() {
      if (this.stageRO) { try { this.stageRO.disconnect() } catch (_) { /* noop */ } this.stageRO = null }
      else window.removeEventListener('resize', this.updateScale)
    },
    updateScale() {
      const wrap = this.$refs.stageWrap
      if (!wrap) return
      const rect = wrap.getBoundingClientRect()
      const padding = 32
      const s = Math.min((rect.width - padding) / 1080, (rect.height - padding) / 1920)
      this.stageScale = isFinite(s) && s > 0 ? s : 1
    },

    /* ── Playback ── */
    selectReel(i) { if (i !== this.activeReelIndex) this.activeReelIndex = i },
    restart() {
      // Reload the bundle (fresh GSAP timeline) and reset audio.
      const reel = this.activeReel
      if (!reel) return
      const audio = this.$refs.audio
      if (audio) { try { audio.pause(); audio.currentTime = 0 } catch (_) { /* noop */ } }
      // Force iframe reload by recreating the Blob URL
      const idx = this.activeReelIndex
      this.activeReelIndex = -1
      this.$nextTick(() => { this.activeReelIndex = idx })
    },
    formatTime(sec) {
      const s = Math.max(0, Math.floor(sec || 0))
      return pad2(Math.floor(s / 60)) + ':' + pad2(s % 60)
    },

    /* ── Recording ── */
    async startRecording() {
      if (!this.activeReel || this.recordingPhase !== 'idle') return
      this.recordingPhase = 'countdown'
      this.countdown = 4
      this.countdownTimer = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer); this.countdownTimer = null
          this.beginCapture()
        }
      }, 1000)
    },
    async beginCapture() {
      try {
        await this.requestFullscreen()
        if (navigator.keyboard && navigator.keyboard.lock) {
          try { await navigator.keyboard.lock(['Escape']) } catch (_) { /* noop */ }
        }

        // 1. Audio routing — must be initialized before getDisplayMedia
        this.ensureAudioRouting()

        // 2. Tab capture at 4K, then Region Capture crop to stage-inner
        this.captureStream = await this.acquireCaptureStream()

        // 3. Build recorder (mix capture video + audio dest)
        this.mediaRecorder = this.buildRecorder(this.captureStream)
        this.recordedChunks = []
        this.mediaRecorder.start(1000)

        this.recordingPhase = 'active'
        // 4. Reload bundle from beginning so GSAP timeline starts fresh on tape
        this.restart()
      } catch (err) {
        console.error('[ShortsPlayer] beginCapture failed', err)
        this.cleanupRecording()
        this.recordingPhase = 'idle'
      }
    },
    ensureAudioRouting() {
      if (this.audioSourceNode) return
      const audio = this.$refs.audio
      if (!audio) return
      const Ctx = window.AudioContext || window.webkitAudioContext
      if (!Ctx) return
      this.audioCtx = new Ctx()
      this.audioSourceNode = this.audioCtx.createMediaElementSource(audio)
      this.audioStreamDest = this.audioCtx.createMediaStreamDestination()
      this.audioSourceNode.connect(this.audioCtx.destination)
      this.audioSourceNode.connect(this.audioStreamDest)
    },
    pickRecorderMime() {
      const candidates = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm']
      for (const m of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m
      }
      return ''
    },
    async acquireCaptureStream() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        throw new Error('getDisplayMedia is not available in this browser')
      }
      const constraints = {
        video: {
          displaySurface: 'browser',
          width: { ideal: 2160, max: 3840 },
          height: { ideal: 3840, max: 4320 },
          frameRate: { ideal: 30 }
        },
        audio: false,
        preferCurrentTab: true,
        surfaceSwitching: 'exclude',
        selfBrowserSurface: 'include'
      }
      const stream = await navigator.mediaDevices.getDisplayMedia(constraints)
      await this.cropStreamToStage(stream)
      return stream
    },
    async cropStreamToStage(stream) {
      const videoTrack = stream.getVideoTracks()[0]
      if (!videoTrack) return
      const CT = window.CropTarget
      if (!CT || typeof CT.fromElement !== 'function' || typeof videoTrack.cropTo !== 'function') {
        console.warn('[ShortsPlayer] Region Capture unavailable — recording whole tab')
        return
      }
      const el = this.$refs.stageInner
      if (!el) return
      try {
        const target = await CT.fromElement(el)
        await videoTrack.cropTo(target)
      } catch (err) {
        console.warn('[ShortsPlayer] cropTo failed', err)
      }
    },
    buildRecorder(videoStream) {
      const audioTracks = this.audioStreamDest ? this.audioStreamDest.stream.getAudioTracks() : []
      const combined = new MediaStream([...videoStream.getVideoTracks(), ...audioTracks])
      const opts = { videoBitsPerSecond: 16_000_000, audioBitsPerSecond: 192_000 }
      const mime = this.pickRecorderMime()
      if (mime) opts.mimeType = mime
      const rec = new MediaRecorder(combined, opts)
      rec.ondataavailable = (e) => { if (e.data && e.data.size) this.recordedChunks.push(e.data) }
      rec.onstop = () => this.finalizeRecording()
      const vt = videoStream.getVideoTracks()[0]
      if (vt) {
        vt.onended = () => {
          if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            try { this.mediaRecorder.stop() } catch (_) { /* noop */ }
          }
        }
      }
      return rec
    },
    stopRecording() {
      if (this.recordingPhase === 'active' && this.mediaRecorder) {
        try { this.mediaRecorder.stop() } catch (_) { /* noop */ }
      } else {
        this.cleanupRecording()
        this.recordingPhase = 'idle'
      }
      const audio = this.$refs.audio
      if (audio) { try { audio.pause() } catch (_) { /* noop */ } }
    },
    finalizeRecording() {
      const blob = new Blob(this.recordedChunks, { type: 'video/webm' })
      this.recordedChunks = []
      const url = URL.createObjectURL(blob)
      const reelId = (this.activeReel && this.activeReel.id) || 'reel'
      const filename = `${this.collectionSlug || 'shorts'}__${reelId}__${timestamp()}.webm`
      const a = document.createElement('a')
      a.href = url; a.download = filename
      document.body.appendChild(a); a.click()
      setTimeout(() => {
        try { document.body.removeChild(a) } catch (_) { /* noop */ }
        try { URL.revokeObjectURL(url) } catch (_) { /* noop */ }
      }, 1500)
      this.cleanupRecording()
      this.recordingPhase = 'idle'
      this.exitFullscreen()
    },
    cleanupRecording() {
      if (this.captureStream) {
        try { this.captureStream.getTracks().forEach(t => t.stop()) } catch (_) { /* noop */ }
        this.captureStream = null
      }
      if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null }
      if (this.reelTimeoutId) { clearTimeout(this.reelTimeoutId); this.reelTimeoutId = null }
      if (navigator.keyboard && navigator.keyboard.unlock) {
        try { navigator.keyboard.unlock() } catch (_) { /* noop */ }
      }
    },
    async requestFullscreen() {
      const root = this.$el
      const fn = root.requestFullscreen || root.webkitRequestFullscreen
      if (fn) { try { await fn.call(root) } catch (_) { /* noop */ } }
    },
    exitFullscreen() {
      const fn = document.exitFullscreen || document.webkitExitFullscreen
      if (fn && document.fullscreenElement) { try { fn.call(document) } catch (_) { /* noop */ } }
    },

    /* ── Keyboard ── */
    onKeyDown(e) {
      if (e.key === 'Escape') {
        if (this.recordingPhase === 'active' || this.recordingPhase === 'countdown') {
          const now = Date.now()
          if (now - this.escTapTime < 500) {
            this.escTapTime = 0
            this.stopRecording()
            this.exitFullscreen()
          } else {
            this.escTapTime = now
            e.preventDefault(); e.stopPropagation()
          }
        }
        return
      }
      if (this.recordingPhase !== 'idle') return
      if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return
      if (e.key === ' ' || e.code === 'Space') { e.preventDefault(); this.restart() }
      else if (e.key === 'ArrowDown') { if (this.activeReelIndex < this.reels.length - 1) this.activeReelIndex += 1 }
      else if (e.key === 'ArrowUp')   { if (this.activeReelIndex > 0) this.activeReelIndex -= 1 }
    },

    goBack() {
      if (this.recordingPhase === 'active') this.stopRecording()
      if (window.history.length > 1) this.$router.back()
      else this.$router.push('/')
    }
  }
}
</script>

<style scoped>
.shorts-player {
  position: fixed; inset: 0;
  background: #141414;
  background-image: radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px);
  background-size: 24px 24px;
  color: #f5f5f5;
  display: flex; flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  overflow: hidden;
}
.shorts-player.recording-active {
  background-image: none;
  background: #000;
}

/* Top bar */
.top-bar {
  height: 64px; flex: 0 0 64px;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 16px;
  background: rgba(20,20,20,0.85);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  z-index: 5;
}
.top-bar-left, .top-bar-center, .top-bar-right { display: flex; align-items: center; gap: 12px; }
.title-block { display: flex; flex-direction: column; line-height: 1.15; }
.collection-title { font-size: 13px; color: #c9c9c9; }
.reel-title { font-size: 16px; font-weight: 600; color: #fff; }
.time-readout { font-variant-numeric: tabular-nums; font-size: 13px; color: #b8b8b8; }
.audio-warn { font-size: 12px; color: #f0b942; padding: 2px 8px; background: rgba(240,185,66,0.12); border-radius: 4px; }

.btn {
  appearance: none; border: 1px solid rgba(255,255,255,0.18);
  background: rgba(255,255,255,0.06); color: #fff;
  padding: 8px 14px; border-radius: 8px;
  font-size: 13px; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.btn:hover:not(:disabled) { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.28); }
.btn:disabled { opacity: 0.45; cursor: not-allowed; }
.btn.primary { background: #D9531F; border-color: #D9531F; }
.btn.primary:hover:not(:disabled) { background: #ee6b39; border-color: #ee6b39; }
.btn.small { padding: 6px 10px; font-size: 12px; }
.btn.ghost { background: transparent; }
.btn-icon {
  appearance: none; border: none;
  background: rgba(255,255,255,0.06); color: #fff;
  width: 36px; height: 36px; border-radius: 8px;
  font-size: 22px; line-height: 1; cursor: pointer;
}
.btn-icon:hover { background: rgba(255,255,255,0.14); }

/* Layout */
.layout { flex: 1 1 auto; display: flex; min-height: 0; }
.stage-wrap {
  flex: 1 1 auto;
  position: relative;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
}
.stage-outer {
  position: relative;
  width: 1080px; height: 1920px;
}
.stage-inner {
  position: absolute; inset: 0;
  width: 1080px; height: 1920px;
  background: #000; border-radius: 24px; overflow: hidden;
  box-shadow: 0 30px 80px rgba(0,0,0,0.55);
}
.shorts-player.recording-active .stage-inner { border-radius: 0; box-shadow: none; }
.bundle-frame {
  position: absolute; inset: 0;
  width: 1080px; height: 1920px;
  border: 0; background: #000;
  display: block;
}
.empty-state {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; gap: 16px; color: #ddd;
}
.empty-title { font-size: 56px; font-weight: 700; }
.empty-sub { font-size: 30px; color: #aaa; }

.countdown-overlay {
  position: absolute; inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  z-index: 4; gap: 32px;
}
.countdown-num { font-size: 360px; font-weight: 800; color: #fff; line-height: 1; }
.countdown-hint { font-size: 28px; color: #ccc; }

.reel-audio { display: none; }

/* Sidebar */
.sidebar {
  flex: 0 0 320px; width: 320px;
  background: rgba(0,0,0,0.35);
  border-left: 1px solid rgba(255,255,255,0.06);
  display: flex; flex-direction: column; min-height: 0;
}
.sidebar-header {
  padding: 14px 18px; font-size: 11px; font-weight: 600;
  letter-spacing: 0.12em; text-transform: uppercase; color: #aaa;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.reel-list {
  flex: 1 1 auto; overflow-y: auto;
  padding: 8px; display: flex; flex-direction: column; gap: 6px;
}
.reel-item {
  appearance: none; border: 1px solid transparent;
  background: rgba(255,255,255,0.04); color: #eee;
  text-align: left; padding: 12px; border-radius: 10px;
  display: flex; gap: 12px; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.reel-item:hover { background: rgba(255,255,255,0.08); }
.reel-item.active { background: rgba(217,83,31,0.18); border-color: rgba(217,83,31,0.5); }
.reel-item-index { font-size: 12px; font-weight: 700; color: #D9531F; width: 28px; flex: 0 0 28px; padding-top: 2px; font-variant-numeric: tabular-nums; }
.reel-item-body { flex: 1 1 auto; min-width: 0; }
.reel-item-title { font-size: 14px; font-weight: 600; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.reel-item-meta { font-size: 11px; color: #888; margin-top: 4px; display: flex; gap: 6px; }

.reel-empty-list { font-size: 13px; color: #999; padding: 14px; text-align: center; }
.reel-empty-list code { background: rgba(255,255,255,0.08); padding: 1px 6px; border-radius: 3px; font-size: 11px; }

.manifest-paste {
  border-top: 1px solid rgba(255,255,255,0.06);
  padding: 12px 14px; display: flex; flex-direction: column; gap: 8px;
}
.manifest-paste-label { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #999; }
.manifest-paste textarea {
  width: 100%; resize: vertical; box-sizing: border-box;
  background: rgba(255,255,255,0.05); color: #eee;
  border: 1px solid rgba(255,255,255,0.1); border-radius: 6px;
  padding: 8px; font-size: 12px;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
}
.manifest-paste-actions { display: flex; gap: 8px; }
.manifest-paste-error { font-size: 12px; color: #ff7b7b; }

.hint {
  font-size: 11px; color: #888; padding: 12px 14px;
  border-top: 1px solid rgba(255,255,255,0.06);
}
.hint kbd {
  display: inline-block; padding: 1px 6px; margin: 0 2px;
  background: rgba(255,255,255,0.08); border-radius: 3px;
  font-family: inherit; font-size: 10px; color: #ddd;
}

/* Recording-active: hide chrome */
.shorts-player.recording-active .top-bar,
.shorts-player.recording-active .sidebar { display: none !important; }
.shorts-player.recording-active .layout { background: #000; }
</style>
